import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useEffect
} from 'react';

export type Sex = 'male' | 'female';

interface ThemeContextValue {
  sex: Sex;
  setSex: (s: Sex) => void;
  isMale: boolean;
  isFemale: boolean;
  primaryColor: string;
  secondaryColor: string;
  backgroundColor: string;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

export const ThemeProvider = ({ children }: { children: ReactNode }) => {
  const getInitialSex = (): Sex => {
    const envSex = import.meta.env.VITE_SEX as Sex;
    if (envSex && (envSex === 'male' || envSex === 'female')) {
      return envSex;
    }
    const storedSex = localStorage.getItem('sex') as Sex;
    if (storedSex && (storedSex === 'male' || storedSex === 'female')) {
      return storedSex;
    }
    return 'female';
  };

  const [sex, setSex] = useState<Sex>(getInitialSex);

  useEffect(() => {
    localStorage.setItem('sex', sex);
  }, [sex]);

  const isMale = sex === 'male';
  const isFemale = sex === 'female';
  
  // Sex label logic corrected:
  // Pink experience = Female
  // Blue experience = Male
  const primaryColor = isFemale ? '#FCF8F9' : '#F9FAFB';
  const secondaryColor = isFemale ? '#EEBECE' : '#B7CBDF';
  const backgroundColor = isFemale ? '#FCF8F9' : '#F9FAFB';

  const value: ThemeContextValue = {
    sex, setSex, isMale, isFemale, primaryColor, secondaryColor, backgroundColor
  };

  return (<ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>);
};

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within a ThemeProvider');
  return ctx;
} 